import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

const SLOTS = Array.from({ length: 24 }, (_, i) => i);
const RADIUS = 130;
const DOT_SIZE = 32;

const COLORS = {
  H: '#d32f2f',
  P: '#1976d2',
  M: '#388e3c',
};

function getSlotItems(r, equipmentObj) {
  const items = [];
  if (String(r) === String(equipmentObj.holster ?? '')) items.push('H');
  if ((equipmentObj.pouches || []).includes(r)) items.push('P');
  if ((equipmentObj.magnets || []).includes(r)) items.push('M');
  return items;
}

export default function EquipmentBelt({ equipmentObj, onChange, readOnly }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSlot, setActiveSlot] = useState(null);

  const handleSlotClick = (e, slot) => {
    if (readOnly) return;
    setAnchorEl(e.currentTarget);
    setActiveSlot(slot);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveSlot(null);
  };

  const handleToggle = (_, newValues) => {
    if (activeSlot == null) return;
    const obj = { ...equipmentObj };
    const prevItems = getSlotItems(activeSlot, equipmentObj);
    const hadH = prevItems.includes('H');
    const hasH = newValues.includes('H');

    // Holster
    if (hasH && !hadH) {
      obj.holster = String(activeSlot);
    } else if (!hasH && hadH) {
      obj.holster = null;
    }

    // Pouches
    obj.pouches = [...(obj.pouches || [])];
    if (newValues.includes('P') && !prevItems.includes('P')) {
      obj.pouches.push(activeSlot);
    } else if (!newValues.includes('P') && prevItems.includes('P')) {
      obj.pouches = obj.pouches.filter((a) => a !== activeSlot);
    }

    // Magnets
    obj.magnets = [...(obj.magnets || [])];
    if (newValues.includes('M') && !prevItems.includes('M')) {
      obj.magnets.push(activeSlot);
    } else if (!newValues.includes('M') && prevItems.includes('M')) {
      obj.magnets = obj.magnets.filter((a) => a !== activeSlot);
    }

    onChange(obj);
  };

  const activeItems = activeSlot != null ? getSlotItems(activeSlot, equipmentObj) : [];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6">{t('registration.equipment', 'Equipment Belt')}</Typography>
      {!readOnly && (
        <Typography variant="caption" color="text.secondary">
          {t('registration.equipmentHint', 'Click a slot on the belt to assign holster, pouches, or magnets')}
        </Typography>
      )}

      {/* Belt circle */}
      <Box sx={{ position: 'relative', width: RADIUS * 2 + DOT_SIZE, height: RADIUS * 2 + DOT_SIZE, mx: 'auto', mt: 2 }} style={{ direction: 'ltr' }}>
        {/* Belt ring */}
        <Box sx={{
          position: 'absolute',
          top: DOT_SIZE / 2,
          left: DOT_SIZE / 2,
          width: RADIUS * 2,
          height: RADIUS * 2,
          borderRadius: '50%',
          border: '3px solid',
          borderColor: 'divider',
        }} />

        {/* Slots */}
        {SLOTS.map((r) => {
          const items = getSlotItems(r, equipmentObj);
          const angle = (-90 + 15 * r) * (Math.PI / 180);
          const cx = RADIUS + RADIUS * Math.cos(angle);
          const cy = RADIUS + RADIUS * Math.sin(angle);
          const hasContent = items.length > 0;
          const isActive = activeSlot === r;

          const bg = hasContent
            ? items.length === 1 ? COLORS[items[0]] : '#7b1fa2'
            : 'background.paper';

          return (
            <Box
              key={r}
              onClick={(e) => handleSlotClick(e, r)}
              sx={{
                position: 'absolute',
                left: cx,
                top: cy,
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: isActive ? 'primary.main' : hasContent ? bg : 'action.disabled',
                bgcolor: bg,
                color: hasContent ? 'white' : 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: hasContent ? 11 : 10,
                fontWeight: hasContent ? 700 : 400,
                cursor: readOnly ? 'default' : 'pointer',
                transition: 'all 0.15s',
                boxShadow: isActive ? 3 : hasContent ? 1 : 0,
                '&:hover': readOnly ? {} : {
                  transform: 'scale(1.15)',
                  boxShadow: 3,
                },
              }}
            >
              {hasContent ? items.join('') : r + 1}
            </Box>
          );
        })}

        {/* "Front" label */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            position: 'absolute',
            top: -18,
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          {t('registration.equipmentForward', 'Front of Belt')}
        </Typography>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        {[
          { key: 'H', label: t('registration.holster', 'Holster'), color: COLORS.H },
          { key: 'P', label: t('registration.pouch', 'Pouch'), color: COLORS.P },
          { key: 'M', label: t('registration.magnet', 'Magnet'), color: COLORS.M },
        ].map(({ key, label, color }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Slot popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 1.5, minWidth: 160 } } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('registration.equipmentSlot', 'Slot {{n}}', { n: activeSlot != null ? activeSlot + 1 : '' })}
        </Typography>
        <ToggleButtonGroup
          value={activeItems}
          onChange={handleToggle}
          size="small"
          sx={{ display: 'flex', gap: 0.5 }}
        >
          <ToggleButton value="H" sx={{ flex: 1, color: COLORS.H, '&.Mui-selected': { bgcolor: COLORS.H, color: 'white', '&:hover': { bgcolor: COLORS.H } } }}>
            {t('registration.holster', 'Holster')}
          </ToggleButton>
          <ToggleButton value="P" sx={{ flex: 1, color: COLORS.P, '&.Mui-selected': { bgcolor: COLORS.P, color: 'white', '&:hover': { bgcolor: COLORS.P } } }}>
            {t('registration.pouch', 'Pouch')}
          </ToggleButton>
          <ToggleButton value="M" sx={{ flex: 1, color: COLORS.M, '&.Mui-selected': { bgcolor: COLORS.M, color: 'white', '&:hover': { bgcolor: COLORS.M } } }}>
            {t('registration.magnet', 'Magnet')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Popover>
    </Box>
  );
}
